import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const pillars = [
  {
    phase: 'Fáza 1',
    duration: 'Mesiac 1–2',
    icon: 'ri-layout-grid-line',
    title: 'Redesign & Technická excelencia',
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/20',
    items: [
      'Kompletný UX audit a wireframing novej štruktúry',
      'WooCommerce migrácia na rýchly headless stack',
      'Core Web Vitals: LCP z 8.2s na 0.9s',
      'Mobilný checkout zredukovaný z 7 krokov na 2',
      'Produktové fotografie a 360° zobrazenia bicyklov',
    ],
  },
  {
    phase: 'Fáza 2',
    duration: 'Mesiac 2–4',
    icon: 'ri-search-eye-line',
    title: 'SEO & Content stratégia',
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    items: [
      'Keyword research - 380 relevantných výrazov',
      'Technické SEO: štruktúrované dáta, sitemapa, hreflang',
      'Blog stratégia: 24 dlhých článkov o cyklistike',
      'Budovanie spätných odkazov z cyklo-portálov',
      'Lokálne SEO pre Bratislavu, Košice, Žilinu',
    ],
  },
  {
    phase: 'Fáza 3',
    duration: 'Mesiac 3–12',
    icon: 'ri-advertisement-line',
    title: 'Performance Marketing',
    color: 'from-violet-500 to-purple-500',
    bgColor: 'bg-violet-500/10',
    borderColor: 'border-violet-500/20',
    items: [
      'Google Shopping kampaň - 1 240 produktov',
      'Meta Ads retargeting s dynamickými produktmi',
      'E-mailový funnel: uvítací, opustený košík, lojalita',
      'Sezónne kampane (jar, leto, Vianoce)',
      'A/B testovanie produktových stránok a CTA',
    ],
  },
];

export default function CaseStrategy() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-900/30 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700/40 to-transparent" />

      {/* bg blobs */}
      <div className="absolute top-1/3 left-0 w-96 h-96 rounded-full bg-orange-500/4 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-0 w-72 h-72 rounded-full bg-violet-500/4 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/40 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-compass-3-line text-orange-400 text-sm" />
            <span className="text-zinc-300 text-sm font-semibold">Naša stratégia</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">
            Tri fázy.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              Jeden cieľ - rast.
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Nie sme agentúra, čo zmení logo a čaká. Každá fáza bola navrhnutá tak,
            aby priniesla merateľné výsledky ešte pred ďalšou.
          </p>
        </div>

        {/* Timeline pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pillars.map((pillar, i) => (
            <div
              key={pillar.phase}
              className={`relative bg-zinc-900 border ${pillar.borderColor} rounded-2xl overflow-hidden transition-all duration-700 hover:-translate-y-1`}
              style={{ transitionDelay: `${i * 120}ms`, opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)' }}
            >
              {/* Top gradient bar */}
              <div className={`h-1 w-full bg-gradient-to-r ${pillar.color}`} />

              <div className="p-7">
                {/* Phase + duration */}
                <div className="flex items-center justify-between mb-5">
                  <span className={`text-xs font-black uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r ${pillar.color}`}>
                    {pillar.phase}
                  </span>
                  <span className="text-zinc-600 text-xs font-medium">{pillar.duration}</span>
                </div>

                {/* Icon + title */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-12 h-12 ${pillar.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <i className={`${pillar.icon} text-xl text-white`} />
                  </div>
                  <h3 className="text-white font-black text-lg leading-tight">{pillar.title}</h3>
                </div>

                {/* Items */}
                <ul className="space-y-3">
                  {pillar.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-emerald-400 text-sm" />
                      </div>
                      <span className="text-zinc-400 text-sm leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Tools used */}
        <div
          className={`mt-14 bg-zinc-900 border border-zinc-800/60 rounded-2xl p-8 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-semibold mb-6">Technológie & nástroje</p>
          <div className="flex flex-wrap gap-3">
            {[
              'WooCommerce', 'WordPress', 'Google Analytics 4', 'Google Ads', 'Google Search Console',
              'Meta Business Suite', 'Klaviyo', 'Hotjar', 'Screaming Frog', 'Ahrefs', 'Figma', 'Cloudflare',
            ].map((tool) => (
              <span key={tool} className="text-xs text-zinc-300 bg-zinc-800 border border-zinc-700/50 rounded-lg px-3 py-1.5 font-medium">
                {tool}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
